import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { bbox } from "@turf/turf";

export default function Map({ routes, selectedAthlete, showEvent, showTraining }) {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const [hovered, setHovered] = useState(null);

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${day}-${month}-${year}`;
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);

        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    };

    const getSpeed = (distanceMeters, movingTimeSeconds) => {
        const km = distanceMeters / 1000;
        const hours = movingTimeSeconds / 3600;

        if (hours === 0) return 0;

        return (km / hours).toFixed(1); // bijv. 27.3 km/h
    };

    useEffect(() => {
        if (!mapInstance.current) return;

        setFilter(selectedAthlete, showTraining, showEvent, mapInstance);

    }, [selectedAthlete, showTraining, showEvent]);

    useEffect(() => {
        if (!routes) return;

        // init map (1x)
        if (!mapInstance.current) {
            mapInstance.current = new maplibregl.Map({
                container: mapRef.current,
                // style: "https://demotiles.maplibre.org/style.json",
                style: "https://tiles.openfreemap.org/styles/liberty"
            });

            mapInstance.current.on("load", () => {
                //const filtered = getFilteredRoutes(routes);
                addRoutes(mapInstance.current, routes);

                let hoveredId = null;

                mapInstance.current.on("mouseenter", "routes", () => {
                    mapInstance.current.getCanvas().style.cursor = "pointer";
                });

                mapInstance.current.on("mousemove", "routes", (e) => {
                    if (e.features.length === 0) {
                        if (hoveredId !== null) {
                            mapInstance.current.setFeatureState(
                                { source: "routes", id: hoveredId },
                                { hover: false }
                            );
                        }
                        hoveredId = null;
                        setHovered(null);
                        return;
                    }

                    const newId = e.features[0].id;

                    if (hoveredId !== null && hoveredId !== newId) {
                        mapInstance.current.setFeatureState(
                            { source: "routes", id: hoveredId },
                            { hover: false }
                        );
                    }

                    hoveredId = newId;

                    mapInstance.current.setFeatureState(
                        { source: "routes", id: hoveredId },
                        { hover: true }
                    );

                    setHovered(e.features[0].properties);
                });

                mapInstance.current.on("mouseleave", "routes", () => {
                    setHovered(null);
                    mapInstance.current.getCanvas().style.cursor = "";
                    if (hoveredId !== null) {
                        mapInstance.current.setFeatureState(
                            { source: "routes", id: hoveredId },
                            { hover: false }
                        );
                    }
                    hoveredId = null;
                });

                setFilter(selectedAthlete, showTraining, showEvent, mapInstance);

            });
        } else {
            // const filtered = getFilteredRoutes(routes);
            addRoutes(mapInstance.current, routes);
        }
    }, [routes]);

    // filter routes based on selectedAthlete, showEvent and showTraining
    /*
    function getFilteredRoutes(routes) {
        console.log("Filtering routes with:", { selectedAthlete, showEvent, showTraining });
        return routes.filter(r => {

            if (selectedAthlete !== "all" && r.athlete !== selectedAthlete) {
                console.log(`Filtering out route ${r.name} by ${r.athlete} because it doesn't match selected athlete ${selectedAthlete}`);
                return false;
            }
            
            if (!showTraining && r.isTraining) {
                console.log(`Filtering out route ${r.name} because it's a training session`);
                return false;
            }

            if (!showEvent && !r.isTraining) {
                console.log(`Filtering out route ${r.name} because it's not an event`);
                return false;
            }

            console.log(`Including route ${r.name} by ${r.athlete}, isTraining: ${r.isTraining} `);

            return true;
        });
    }
    */
    
    function setFilter(selectedAthlete, showTraining, showEvent, mapInstance) {
        const filters = ["all"];
    
        if (selectedAthlete !== "all") {
            filters.push([
                "==",
                ["get", "athlete"],
                selectedAthlete
            ]);
        }
    
        if (!showTraining) {
            filters.push([
                "!=",
                ["get", "isTraining"],
                true
            ]);
        }
    
        if (!showEvent) {
            filters.push([
                "!=",
                ["get", "isTraining"],
                false
            ]);
        }
    
        mapInstance.current.setFilter("routes", filters);
    }

    function addRoutes(map, routes) {
        console.log("Adding routes to map:", routes);
        const features = routes.map(r => ({
            type: "Feature",
            id: r.id,
            properties: {
                name: r.name,
                nickname: r.nickname,
                firstName: r.firstName,
                lastName: r.lastName,
                displayName: r.displayName,
                date: r.date,
                description: r.description,
                duration: r.duration,
                athlete: r.athlete,
                profilePhoto: r.profilePhoto,
                isTraining: r.isTraining,
                distance: (Number(r.distance) / 1000).toFixed(2), // convert to km,
            },
            geometry: {
                type: "LineString",
                coordinates: r.points.map(p => {
                    let lon = p[1];
                    if (lon > 180) lon -= 360;
                    if (lon < -180) lon += 360;
                    return [lon, p[0]]; // lon, lat
                })
            }
        }));

        const geojson = {
            type: "FeatureCollection",
            features
        };

        if (map.getSource("routes")) {
            map.getSource("routes").setData(geojson);
        } else {
            map.addSource("routes", {
                type: "geojson",
                data: geojson
            });

            map.addLayer({
                id: "routes",
                type: "line",
                source: "routes",
                paint: {
                    "line-color": "#ff0000",
                    "line-width": [
                        "case",
                        ["boolean", ["feature-state", "hover"], false],
                        6,
                        3
                    ],
                    "line-opacity": [
                        "case",
                        ["boolean", ["feature-state", "hover"], false],
                        1.0,
                        0.3
                    ]
                }
            });
        }

        // TODO: fit bounds to the Netherlads instead of all routes,
        // if there are no routes.
        if (features.length === 0) return;

        const bounds = bbox(geojson); // geojson = FeatureCollection
        map.fitBounds([
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]]
        ], {
            padding: 50,
            maxZoom: 9
        });
    }
    // className="map-container"
    return <div style={{ position: "absolute", top: 0, height: "100vh", width: "100%", zIndex: 0 }}>
        <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
        {hovered && (
            <div className="map-info">
                <strong>{hovered.name}</strong><br />
                <em>{hovered.description}</em><br />
                <small>{hovered.displayName}</small><br />
                {hovered.profilePhoto && (
                    <img src={hovered.profilePhoto} alt="Profielfoto" className="profile-photo" />
                )}
                <label>Datum:</label>{formatDate(hovered.date)}<br />
                <label>Afstand:</label>{hovered.distance} km<br />
                <label>Duur:</label>{formatTime(hovered.duration)}<br />
                <label>Gem. snelheid:</label>{getSpeed(hovered.distance * 1000, hovered.duration)} km/h
            </div>
        )}
    </div>; ////<div ref={mapRef} style={{ height: "500px", width: "100%" }} />;
}

